import React, { useState } from 'react';
import { PartCardData } from '../../hooks/useAgentConversation';
import { displayPartNumber } from '../../utils/displayPartNumber';

interface PartCardProps {
  card: PartCardData;
  onAddToCart?: (partNumber: string) => void;
}

const AVAILABILITY_COPY: Record<string, string> = {
  in_stock: 'In stock',
  transfer_needed: 'Transfer needed',
  vendor_order_needed: 'Vendor order',
};

const AVAILABILITY_VARIANT: Record<string, string> = {
  in_stock: 'in-stock',
  transfer_needed: 'transfer',
  vendor_order_needed: 'vendor',
};

const formatPrice = (price: number | null | undefined): string => {
  if (price === null || price === undefined) return 'Contact for pricing';
  return `$${Number(price).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const PartCard: React.FC<PartCardProps> = ({ card, onAddToCart }) => {
  const { data } = card;
  const [imageBroken, setImageBroken] = useState(false);

  const availabilityLabel =
    AVAILABILITY_COPY[data.availability_status] || data.availability_status;
  const availabilityVariant =
    AVAILABILITY_VARIANT[data.availability_status] || 'vendor';

  const description = data.description?.trim() || null;
  const longDescription = data.long_description?.trim() || null;
  const truncatedLong =
    longDescription && longDescription.length > 140
      ? `${longDescription.slice(0, 140)}…`
      : longDescription;

  const branchCopy = (() => {
    const qty = data.quantity_at_branch;
    const total = data.quantity_across_all;
    if (!data.location_name) {
      // No branch context (parts-order widget: pickup location is chosen at
      // review) — show org-wide stock only.
      return total > 0
        ? `${total} available across our locations`
        : 'Currently out of stock';
    }
    const loc = data.location_name;
    if (qty > 0) {
      return `${qty} on hand at ${loc}${total > qty ? ` (${total} across branches)` : ''}`;
    }
    if (total > 0) {
      return `Not at ${loc} — ${total} available across other branches`;
    }
    return `Currently out of stock`;
  })();

  const compatibility = data.compatibility || [];

  return (
    <div className="part-card">
      <div className="part-card__body">
        {data.image_url && !imageBroken && (
          <div className="part-card__thumb">
            <img
              src={data.image_url}
              alt={description || displayPartNumber(data.part_number)}
              loading="lazy"
              onError={() => setImageBroken(true)}
            />
          </div>
        )}

        <div className="part-card__details">
          <div className="part-card__header">
            <span className="part-card__title">{displayPartNumber(data.part_number)}</span>
            <span
              className={`part-card__badge part-card__badge--${availabilityVariant}`}
            >
              {availabilityLabel}
            </span>
          </div>

          {description && (
            <div className="part-card__description">{description}</div>
          )}
          {truncatedLong && truncatedLong !== description && (
            <p className="part-card__long-description">{truncatedLong}</p>
          )}

          <div className="part-card__price">{formatPrice(data.unit_price)}</div>

          <div className="part-card__meta-line">{branchCopy}</div>

          {compatibility.length > 0 && (
            <div className="part-card__compatibility">
              <span className="part-card__compatibility-label">Fits:</span>
              {compatibility.slice(0, 3).map((m, i) => (
                <span key={i} className="part-card__compatibility-tag">
                  {m}
                </span>
              ))}
              {compatibility.length > 3 && (
                <span className="part-card__compatibility-more">
                  +{compatibility.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {onAddToCart && (
        <button
          type="button"
          className="part-card__cta"
          onClick={() => onAddToCart(data.part_number)}
        >
          Add to cart
        </button>
      )}
    </div>
  );
};

export default PartCard;
